/** Temporary hotel-only reset: single numeric SSOT from API-facing fields on the package */

import { resolveHotelPackageDisplayCurrencies } from "./resolveHotelPackageDisplayCurrencies";

export const PRICE_PIPELINE_MODE_HOTEL_API_ONLY = "hotel_api_price_only";

/**
 * ממלא/משלים displayCurrencies על החבילה בטיוטה (כולל hasUsd / formattedUsd) כדי שלא יאבדו ב-sidebar.
 * חובה: לא לבנות מחדש ולא לגעת ב־pkg.Rooms — זהות חדרי הספק (Rooms[].Id) חייבת להישאר כמו ב־hotelInformation.
 */
export function attachResolvedDisplayCurrenciesToHotelPackage(pkg) {
  if (!pkg || typeof pkg !== "object") return pkg;
  const resolved = resolveHotelPackageDisplayCurrencies(pkg);
  const ex =
    pkg.displayCurrencies && typeof pkg.displayCurrencies === "object"
      ? pkg.displayCurrencies
      : {};

  const ilsFromEx = ex.ils != null && ex.ils !== "" ? Number(ex.ils) : null;
  const formattedIlsFromEx =
    typeof ex.formattedIls === "string" && ex.formattedIls.trim() !== ""
      ? ex.formattedIls.trim()
      : null;
  const formattedUsdFromEx =
    typeof ex.formattedUsd === "string" && ex.formattedUsd.trim() !== ""
      ? ex.formattedUsd.trim()
      : null;
  const hasUsdExplicit = typeof ex.hasUsd === "boolean";

  const ils =
    Number.isFinite(ilsFromEx) && ilsFromEx > 0 ? ilsFromEx : resolved.ils;
  const formattedIls = formattedIlsFromEx ?? resolved.formattedIls;
  const formattedUsd =
    formattedUsdFromEx ??
    (typeof resolved.formattedUsd === "string" && resolved.formattedUsd.trim() !== ""
      ? resolved.formattedUsd.trim()
      : "");

  let hasUsd = hasUsdExplicit ? ex.hasUsd : resolved.hasUsd;
  if (!hasUsdExplicit && String(formattedUsd).trim() !== "") {
    hasUsd = true;
  }

  const out = {
    ils,
    formattedIls,
    hasUsd,
    formattedUsd: String(formattedUsd),
  };
  const usdPick =
    ex.usd != null && Number.isFinite(Number(ex.usd))
      ? Number(ex.usd)
      : resolved.usd != null && Number.isFinite(Number(resolved.usd))
        ? Number(resolved.usd)
        : null;
  if (usdPick != null) out.usd = usdPick;

  const srcPick =
    typeof ex.usdSource === "string" && ex.usdSource.trim() !== ""
      ? ex.usdSource.trim()
      : resolved.usdSource || null;
  if (srcPick) out.usdSource = srcPick;

  return {
    ...pkg,
    displayCurrencies: out,
  };
}

export function resolveHotelApiPriceOnlyIlsFromPackage(pkg) {
  if (!pkg || typeof pkg !== "object") return null;
  const dcRaw = pkg.displayCurrencies?.ils;
  if (dcRaw != null && dcRaw !== "") {
    const n = Number(dcRaw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const pwccfRaw = pkg.ourPrice?.priceWithCommissionAndCreditCardFee;
  if (pwccfRaw != null && pwccfRaw !== "") {
    const n = Number(pwccfRaw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * Hotel-only agent profile / checkout — excludes public, vacation package UX, DP profile rooms.
 */
export function shouldApplyHotelApiPriceOnlyDraft(ctx = {}) {
  if (ctx.publicMode === true || ctx.publicPricingMode === true) return false;
  if (ctx.packageExperience === true) return false;
  if (ctx.searchPhrase?.dynamic === true) return false;
  if (ctx.dynamicPackageProfileRoomsUx === true) return false;
  if (ctx.dynamicPackageRoomSupplementUx === true) return false;
  if (ctx.dynamicPackageSupplementalPricing === true) return false;
  return true;
}

export function logHotelApiPriceOnlyTrace(stage, payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  const pkgId =
    payload.packageId ??
    payload.pkg?.packageID ??
    payload.pkg?.PackageId ??
    payload.pkg?.packageId ??
    null;
  // eslint-disable-next-line no-console
  console.error("[HOTEL_API_PRICE_ONLY_TRACE]", {
    stage,
    packageId: pkgId,
    apiPriceIls: payload.apiPriceIls ?? null,
    bookingSsotIls: payload.bookingSsotIls ?? null,
    selectedPriceSnapshotFinal:
      payload.selectedPriceSnapshotFinal ??
      payload.selectedPriceSnapshot?.finalPriceIls ??
      null,
    renderedPrice: payload.renderedPrice ?? null,
    source: payload.source ?? stage,
  });
}

/**
 * @returns {{ bookingSsotIls: number, selectedPriceSnapshot: object, pricePipelineMode: string } | null}
 */
export function tryBuildHotelApiPriceOnlyDraftPatch(pkg, ctx = {}) {
  if (!shouldApplyHotelApiPriceOnlyDraft(ctx)) return null;
  const raw = resolveHotelApiPriceOnlyIlsFromPackage(pkg);
  if (raw == null || !Number.isFinite(Number(raw)) || Number(raw) <= 0) return null;
  const bookingSsotIls = Math.ceil(Number(raw));
  const selectedPriceSnapshot = {
    finalPriceIls: bookingSsotIls,
    source: PRICE_PIPELINE_MODE_HOTEL_API_ONLY,
  };
  logHotelApiPriceOnlyTrace("hotel_profile_draft_patch", {
    packageId: pkg?.packageID ?? pkg?.PackageId ?? pkg?.packageId,
    apiPriceIls: Number(raw),
    bookingSsotIls,
    selectedPriceSnapshotFinal: bookingSsotIls,
    renderedPrice: null,
    source: "tryBuildHotelApiPriceOnlyDraftPatch",
  });
  return {
    bookingSsotIls,
    selectedPriceSnapshot,
    pricePipelineMode: PRICE_PIPELINE_MODE_HOTEL_API_ONLY,
  };
}

export function isHotelApiPriceOnlyOrder(order) {
  return (
    order &&
    typeof order === "object" &&
    order.pricePipelineMode === PRICE_PIPELINE_MODE_HOTEL_API_ONLY
  );
}
